import React from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Navigate, useLocation } from 'react-router-dom';
import authConfig from './config';

/**
 * ProtectedRoute component that redirects unauthenticated users to sign in
 * 
 * Only routes specified in authConfig.protectedRoutes require authentication.
 * All other routes are accessible to both anonymous and authenticated users.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render when authenticated
 * @returns {React.ReactNode} Protected route component
 */
const ProtectedRoute = ({ children }) => {
  const { isLoaded, userId } = useAuth();
  const location = useLocation();
  
  // Check if the current path requires authentication
  const requiresAuth = authConfig.protectedRoutes.some(route => {
    if (route.endsWith('/*')) {
      // Handle wildcard routes
      const basePath = route.replace('/*', '');
      return location.pathname.startsWith(basePath);
    }
    return location.pathname === route;
  });
  
  // If path doesn't require auth, render content regardless of auth status
  if (!requiresAuth) {
    return children;
  }

  // Show loading state while Clerk is initializing
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // For protected routes, redirect to sign-in if not authenticated
  if (!userId) {
    // Save the current location to redirect back after sign-in
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // User is authenticated, render the protected content
  return children;
};

export default ProtectedRoute;
